import { useEffect, useState } from "react";

import { debounce } from "lodash";

import {
  FETCH_ORDER_TAG_EXCLUSIONS,
  UPDATE_TAG_BASED_ORDER_EXCLUSIONS,
} from "@gql/order-tag-filters";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type FetchTagBasedOrderExclusions as FetchTagBasedOrderExclusionsQuery,
  type UpdateTagBasedOrderExclusions,
  type UpdateTagBasedOrderExclusionsVariables,
} from "@north-beam/nb-client-side";
import { type TagBasedOrderExclusion } from "@north-beam/nb-common";

export const useOrderTagExclusions = () => {
  const [isSynced, setIsSynced] = useState<boolean>(true);

  const [tagBasedOrderExclusions, setTagBasedOrderExclusions] = useState<
    TagBasedOrderExclusion[]
  >([]);

  const { data, loading } =
    useNorthbeamQuery<FetchTagBasedOrderExclusionsQuery>(
      FETCH_ORDER_TAG_EXCLUSIONS,
    );

  const [updateTagBasedOrderExclusions] = useNorthbeamMutation<
    UpdateTagBasedOrderExclusions,
    UpdateTagBasedOrderExclusionsVariables
  >(UPDATE_TAG_BASED_ORDER_EXCLUSIONS);

  useEffect(() => {
    if (data?.me?.tagBasedOrderExclusions) {
      setTagBasedOrderExclusions((prevTagBasedOrderExclusions) => {
        if (prevTagBasedOrderExclusions.length === 0) {
          return data.me.tagBasedOrderExclusions;
        }
        return prevTagBasedOrderExclusions;
      });
    }
  }, [data?.me?.tagBasedOrderExclusions]);

  const debouncedUpdate = debounce(
    async (newData: TagBasedOrderExclusion[]) => {
      setIsSynced(false);
      await updateTagBasedOrderExclusions({
        variables: { tagBasedOrderExclusions: newData },
        refetchQueries: [{ query: FETCH_ORDER_TAG_EXCLUSIONS }],
      });
      setIsSynced(true);
    },
    250,
    {
      leading: false,
      trailing: true,
    },
  );

  const updateTagOrderExclusionByIndex = (
    index: number,
    newData: TagBasedOrderExclusion,
  ) => {
    const copyExclusions = [...tagBasedOrderExclusions];
    copyExclusions[index] = newData;
    setTagBasedOrderExclusions(copyExclusions);
    debouncedUpdate(copyExclusions);
  };

  const createTagOrderExclusions = () => {
    const newExclusion: TagBasedOrderExclusion = {
      name: "",
      tagType: "order",
      pattern: "",
      tagLogic: "is",
      active: false,
    };
    const copyExclusions = [...tagBasedOrderExclusions, newExclusion];
    setTagBasedOrderExclusions(copyExclusions);
    debouncedUpdate(copyExclusions);
  };

  const removeTagBasedOrderExclusion = (index: number) => {
    const copyExclusions = [...tagBasedOrderExclusions];
    copyExclusions.splice(index, 1);
    setTagBasedOrderExclusions(copyExclusions);
    debouncedUpdate(copyExclusions);
  };

  return {
    isSynced,
    loading,
    tagBasedOrderExclusions,
    updateTagOrderExclusionByIndex,
    createTagOrderExclusions,
    removeTagBasedOrderExclusion,
  };
};
